<template>
  <div class="production-orders">
    <div class="page-heading">
      <div><h1>订单建卷</h1><p>按订单生成布卷，一卷一码，扫码后开始生产。</p></div>
      <Button v-if="canCreate" type="primary" icon="md-add" :disabled="!factoryId || creating || factoriesLoading" @click="openCreate">新建订单 / 追加布卷</Button>
    </div>
    <Card dis-hover>
      <div class="filters">
        <label>所属工厂<Select v-model="factoryId" filterable :disabled="creating || createVisible" placeholder="选择工厂" @on-change="changeFactory"><Option v-for="item in factories" :key="item.id" :value="item.id">{{ item.name }}</Option></Select></label>
        <label>订单号<Input v-model="orderNo" :maxlength="100" clearable placeholder="输入完整订单号" @on-enter="search" /></label>
        <label>卷号<Input v-model="rollNo" :maxlength="100" clearable placeholder="输入完整卷号" @on-enter="search" /></label>
        <label>状态<Select v-model="status" clearable placeholder="全部状态"><Option value="READY">待生产</Option><Option value="ACTIVE">生产中</Option><Option value="FINISHED">已完成</Option></Select></label>
        <label class="production-time-filter">生产开始时间（北京时间）<DatePicker :value="productionTimeRange" type="datetimerange" format="yyyy-MM-dd HH:mm:ss" clearable placeholder="开始时间（含）至结束时间（不含）" @on-change="productionTimeRange = $event" /></label>
        <Button type="primary" :disabled="!factoryId" :loading="loading" @click="search">查询</Button>
        <Button :disabled="creating || createVisible" :loading="factoriesLoading" @click="loadFactories">刷新工厂</Button>
      </div>
    </Card>
    <Alert v-if="error" type="error" show-icon>{{ error }}</Alert>
    <div v-if="summary" class="summary">
      <Card v-for="item in summaryCards" :key="item.key" dis-hover><span>{{ item.title }}</span><strong>{{ summary[item.key] || 0 }}<small>卷</small></strong></Card>
    </div>
    <Alert v-if="createdBatch" type="success" show-icon>
      订单 {{ createdBatch.orderNo }} 本次已生成 {{ createdBatch.quantity }} 卷，可下载二维码后打印。
      <Button type="text" :loading="downloadingBatch === createdBatch.batchId" @click="downloadBatch(createdBatch.batchId)">下载本次全部二维码</Button>
    </Alert>
    <Card dis-hover>
      <div slot="title">布卷记录 <span class="muted">{{ appliedOrderNo ? '订单 ' + appliedOrderNo : '当前工厂全部订单' }}</span></div>
      <p class="expand-hint">点击左侧箭头展开人员分段</p>
      <Table :columns="columns" :data="rows" :loading="loading" no-data-text="暂无布卷，请选择工厂并创建订单或调整筛选条件" />
      <div class="table-footer"><span>共 {{ total }} 卷</span><Page :current="page" :total="total" :page-size="pageSize" :page-size-opts="[20, 50, 100]" show-sizer @on-change="changePage" @on-page-size-change="changePageSize" /></div>
    </Card>
    <FabricDetail v-if="detailVisible" :factory-id="factoryId" :fabric-id="detailFabricId" @close="detailVisible = false" />
    <Modal v-model="createVisible" title="创建订单并生成布卷" :width="740" :mask-closable="false" :closable="!creating">
      <p class="form-hint">同一工厂下相同订单号归为同一订单，可继续追加布卷。每次最多生成 500 卷。</p>
      <Alert v-if="pending" type="warning" show-icon>本次生成结果尚未确认。请重试完成本次操作，已生成的布卷不会重复创建。</Alert>
      <Alert v-if="createError" type="error" show-icon>{{ createError }}</Alert>
      <Form :label-width="88" class="create-form">
        <FormItem label="订单号" required><Input v-model="form.orderNo" :disabled="formLocked" :maxlength="100" placeholder="例如 SO20261022-001" /></FormItem>
        <FormItem label="客户"><Input v-model="form.customerName" :disabled="formLocked" :maxlength="200" placeholder="客户名称（选填）" /></FormItem>
        <FormItem label="布号" required><Input v-model="form.fabricNo" :disabled="formLocked" :maxlength="100" placeholder="布料款号 / 布号" /></FormItem>
        <FormItem label="布料类型" required><Input v-model="form.fabricType" :disabled="formLocked" :maxlength="200" placeholder="例如 单面针织布" /></FormItem>
        <FormItem label="生成卷数" required><InputNumber v-model="form.quantity" :disabled="formLocked" :min="1" :max="500" :precision="0" /></FormItem>
        <FormItem label="批次号"><Input v-model="form.batchNo" :disabled="formLocked" :maxlength="100" placeholder="选填" /></FormItem>
        <FormItem label="颜色"><Input v-model="form.colorName" :disabled="formLocked" :maxlength="100" placeholder="颜色名称（选填）" /></FormItem>
        <FormItem label="色号"><Input v-model="form.colorCode" :disabled="formLocked" :maxlength="100" placeholder="选填" /></FormItem>
        <FormItem label="门幅 cm"><InputNumber v-model="form.widthCm" :disabled="formLocked" :min="0" :max="999999999.999" :precision="3" placeholder="选填" /></FormItem>
        <FormItem label="克重 g/㎡"><InputNumber v-model="form.gramWeightGsm" :disabled="formLocked" :min="0" :max="999999999.999" :precision="3" placeholder="选填" /></FormItem>
        <FormItem label="成分" class="full"><Input v-model="form.composition" :disabled="formLocked" :maxlength="500" placeholder="选填" /></FormItem>
        <FormItem label="备注" class="full"><Input v-model="form.remark" :disabled="formLocked" :maxlength="1000" type="textarea" :rows="2" placeholder="选填" /></FormItem>
      </Form>
      <div slot="footer"><Button :disabled="creating" @click="createVisible = false">关闭</Button><Button type="primary" :loading="creating" @click="submitCreate">{{ pending ? '重试生成' : '生成布卷及二维码' }}</Button></div>
    </Modal>
    <Modal v-model="qrVisible" title="布卷二维码" :width="420" @on-visible-change="onQrVisible">
      <div class="qr-preview"><Spin v-if="qrLoading" fix /><p v-if="qrRow">{{ qrRow.rollNo }}</p><img v-if="qrUrl" :src="qrUrl" alt="布卷二维码" /><p v-if="qrRow" class="muted">订单 {{ qrRow.orderNo || '—' }}</p><Alert v-if="qrError" type="error">{{ qrError }}</Alert></div>
      <div slot="footer"><Button @click="qrVisible = false">关闭</Button><Button type="primary" :disabled="!qrBlob" @click="downloadQr">下载 PNG</Button></div>
    </Modal>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'
import FabricDetail from './fabric-detail.vue'
import FabricSegments from './fabric-segments.vue'
import { productionFactories, createFabricBatch, listFabrics, fabricOrderSummary, fabricQr, fabricBatchZip, productionData, productionError, productionBlob, newBatchId, fabricBatchPayload } from '@/api/production'

const emptyForm = () => ({ orderNo: '', fabricNo: '', fabricType: '', customerName: '', quantity: 1, batchNo: '', colorName: '', colorCode: '', widthCm: null, gramWeightGsm: null, composition: '', remark: '' })
const statuses = { READY: '待生产', ACTIVE: '生产中', FINISHED: '已完成' }

export default {
  name: 'production_orders',
  components: { FabricDetail },
  data () {
    return {
      factories: [],
      factoriesLoading: false,
      factoryId: null,
      orderNo: '',
      rollNo: '',
      status: '',
      productionTimeRange: [],
      appliedOrderNo: '',
      appliedFilters: {},
      page: 1,
      pageSize: 20,
      total: 0,
      rows: [],
      summary: null,
      error: '',
      loading: false,
      listSequence: 0,
      factorySequence: 0,
      qrSequence: 0,
      createVisible: false,
      creating: false,
      createError: '',
      form: emptyForm(),
      pending: null,
      createdBatch: null,
      detailVisible: false,
      detailFabricId: '',
      downloadingBatch: '',
      qrVisible: false,
      qrLoading: false,
      qrRow: null,
      qrUrl: '',
      qrBlob: null,
      qrError: '',
      summaryCards: [{ key: 'totalCount', title: '订单总卷数' }, { key: 'readyCount', title: '待生产' }, { key: 'activeCount', title: '生产中' }, { key: 'finishedCount', title: '已完成' }]
    }
  },
  computed: {
    canCreate () { return [].concat(this.$store.state.user.access || []).some(role => ['admin', 'super_admin', 'manager', 'Store Manager'].includes(role)) },
    formLocked () { return this.creating || !!this.pending },
    columns () {
      return [
        { type: 'expand', width: 48, render: (h, { row }) => h(FabricSegments, { key: `${this.factoryId}:${row.fabricId}`, props: { factoryId: this.factoryId, fabricId: row.fabricId, rollNo: row.rollNo || '', status: row.status } }) },
        { title: '订单号', key: 'orderNo', minWidth: 165 },
        { title: '卷号', key: 'rollNo', minWidth: 175 },
        { title: '布号', key: 'fabricNo', minWidth: 130 },
        { title: '布料类型', key: 'fabricType', minWidth: 120 },
        { title: '颜色', key: 'colorName', minWidth: 90 },
        { title: '客户', key: 'customerName', minWidth: 120 },
        { title: '状态', width: 100, render: (h, { row }) => h('Tag', { props: { color: row.status === 'ACTIVE' ? 'blue' : row.status === 'FINISHED' ? 'green' : 'default' } }, statuses[row.status] || row.status) },
        { title: '累计圈数', key: 'totalRounds', minWidth: 100 },
        { title: '分段数', key: 'segmentCount', minWidth: 85 },
        { title: '已上传瑕疵', key: 'uploadedDefectCount', minWidth: 110 },
        { title: '操作',
          width: 280,
          render: (h, { row }) => h('div', [
            h('Button', { props: { type: 'text', size: 'small' }, on: { click: () => this.showDetail(row) } }, '查看详情'),
            h('Button', { props: { type: 'text', size: 'small' }, on: { click: () => this.showQr(row) } }, '二维码'),
            h('Button', { props: { type: 'text', size: 'small', disabled: !row.creationBatchId || !!this.downloadingBatch, loading: this.downloadingBatch === row.creationBatchId }, on: { click: () => this.downloadBatch(row.creationBatchId) } }, '同批二维码 ZIP')
          ]) }
      ]
    }
  },
  mounted () { this.loadFactories() },
  beforeDestroy () { this.listSequence++; this.factorySequence++; this.closeQr() },
  methods: {
    async loadFactories () {
      const sequence = ++this.factorySequence
      this.factoriesLoading = true
      try {
        const factories = []
        let page = 1
        while (true) {
          const data = productionData(await productionFactories({ page, pageSize: 100 }))
          if (sequence !== this.factorySequence) return
          factories.push(...(data.records || []))
          if (factories.length >= data.total || !(data.records || []).length) break
          page++
        }
        this.factories = factories.map(item => ({ id: Number(item.id), name: item.name }))
        if (!this.factories.some(item => item.id === this.factoryId)) {
          this.factoryId = this.factories.length ? this.factories[0].id : null
          this.changeFactory()
        }
        if (!this.factories.length) this.error = '暂无可访问的工厂，请联系管理员'
      } catch (error) { if (sequence === this.factorySequence) this.error = productionError(error) } finally {
        if (sequence === this.factorySequence) this.factoriesLoading = false
      }
    },
    changeFactory () {
      this.detailVisible = false; this.detailFabricId = ''
      this.closeQr(); this.qrVisible = false
      this.createdBatch = null; this.pending = null; this.summary = null
      this.page = 1; this.orderNo = ''; this.rollNo = ''; this.status = ''; this.appliedOrderNo = ''; this.appliedFilters = {}
      this.productionTimeRange = []
      this.loadRows()
    },
    search () {
      const [start, end] = this.productionTimeRange || []
      if ((start || end) && (!start || !end || start >= end)) {
        this.error = '请选择完整的生产开始时间范围，起点必须早于终点'
        return
      }
      this.page = 1
      this.appliedOrderNo = this.orderNo.trim()
      this.appliedFilters = { ...(this.appliedOrderNo ? { orderNo: this.appliedOrderNo } : {}), ...(this.rollNo.trim() ? { rollNo: this.rollNo.trim() } : {}), ...(this.status ? { status: this.status } : {}) }
      if (start && end) {
        this.appliedFilters.productionFrom = start.replace(' ', 'T') + '+08:00'
        this.appliedFilters.productionTo = end.replace(' ', 'T') + '+08:00'
      }
      return this.loadRows()
    },
    async loadRows () {
      const sequence = ++this.listSequence
      const factoryId = this.factoryId
      this.rows = []; this.total = 0; this.summary = null; this.error = ''
      if (!factoryId) { this.loading = false; return }
      this.loading = true
      try {
        const results = await Promise.all([
          listFabrics({ factoryId, page: this.page, pageSize: this.pageSize, ...this.appliedFilters }).then(productionData),
          this.appliedOrderNo ? fabricOrderSummary(factoryId, this.appliedOrderNo).then(productionData) : Promise.resolve(null)
        ])
        if (sequence !== this.listSequence || factoryId !== this.factoryId) return
        this.rows = results[0].items || []; this.total = Number(results[0].total) || 0; this.summary = results[1]
      } catch (error) { if (sequence === this.listSequence) this.error = productionError(error) } finally {
        if (sequence === this.listSequence) this.loading = false
      }
    },
    changePage (page) { this.page = page; this.loadRows() },
    changePageSize (size) { this.pageSize = size; this.page = 1; this.loadRows() },
    pendingKey (factoryId) { return `production-order-pending:${this.$store.state.user.userId}:${factoryId}` },
    openCreate () {
      if (!this.factoryId || !this.canCreate) return
      this.createError = ''; this.form = { ...emptyForm(), orderNo: this.appliedOrderNo }; this.pending = null
      this.createVisible = true
      try {
        const saved = window.sessionStorage.getItem(this.pendingKey(this.factoryId))
        if (saved) {
          this.pending = JSON.parse(saved)
          this.form = { ...emptyForm(), ...this.pending }
        }
      } catch (_) { this.createError = '无法恢复上次生成操作，请检查浏览器存储权限后重试' }
    },
    async submitCreate () {
      if (this.creating || !this.factoryId || !this.canCreate) return
      const factoryId = this.factoryId
      const key = this.pendingKey(factoryId)
      this.createError = ''
      let payload
      try {
        // Persist before sending: a timeout/reload must reuse exactly the same request and UUID.
        const saved = window.sessionStorage.getItem(key)
        payload = saved ? JSON.parse(saved) : { ...fabricBatchPayload(this.form), requestId: newBatchId() }
        window.sessionStorage.setItem(key, JSON.stringify(payload))
      } catch (error) { this.createError = error.message || '无法保存本次操作，请检查浏览器存储权限'; return }
      this.pending = payload; this.creating = true
      try {
        const result = productionData(await createFabricBatch(factoryId, payload))
        window.sessionStorage.removeItem(key)
        this.pending = null
        if (factoryId !== this.factoryId) return
        this.createdBatch = result; this.createVisible = false
        this.orderNo = payload.orderNo; this.rollNo = ''; this.status = ''
        this.productionTimeRange = []
        this.$Message.success(`已生成 ${result.quantity} 卷布及二维码`)
        await this.search()
      } catch (error) {
        const status = error.response && error.response.status
        // Definitive input/permission rejection has not created a batch. Unknown results retain the retry key.
        if ([400, 401, 403, 404].includes(status)) {
          window.sessionStorage.removeItem(key); this.pending = null
        }
        this.createError = productionError(error)
      } finally { this.creating = false }
    },
    showDetail (row) { this.detailFabricId = row.fabricId; this.detailVisible = true },
    closeQr () {
      this.qrSequence++
      if (this.qrUrl) URL.revokeObjectURL(this.qrUrl)
      this.qrUrl = ''; this.qrBlob = null; this.qrLoading = false
    },
    onQrVisible (visible) { if (!visible) this.closeQr() },
    async showQr (row) {
      this.closeQr()
      const sequence = this.qrSequence
      this.qrRow = row; this.qrVisible = true; this.qrLoading = true; this.qrError = ''
      try {
        const blob = await productionBlob(await fabricQr(this.factoryId, row.fabricId), 'image/png')
        if (sequence !== this.qrSequence) return
        this.qrBlob = blob; this.qrUrl = URL.createObjectURL(blob)
      } catch (error) { if (sequence === this.qrSequence) this.qrError = productionError(error) } finally {
        if (sequence === this.qrSequence) this.qrLoading = false
      }
    },
    downloadQr () { if (this.qrBlob && this.qrRow) saveAs(this.qrBlob, `fabric-${this.qrRow.fabricId}.png`) },
    async downloadBatch (batchId) {
      if (!batchId || this.downloadingBatch) return
      const factoryId = this.factoryId
      this.downloadingBatch = batchId
      try {
        const blob = await productionBlob(await fabricBatchZip(factoryId, batchId), 'application/zip')
        if (factoryId === this.factoryId) saveAs(blob, `fabric-batch-${batchId}.zip`)
      } catch (error) { if (factoryId === this.factoryId) this.$Message.error(productionError(error)) } finally { this.downloadingBatch = '' }
    }
  }
}
</script>

<style scoped>
.production-orders { padding: 8px; }
.expand-hint { margin-bottom: 12px; color: #808695; }
.page-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; gap: 16px; }
h1 { font-size: 24px; color: #17233d; margin-bottom: 6px; }
.page-heading p, .muted, .form-hint { color: #808695; }
.filters { display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end; }
.filters label { display: flex; flex-direction: column; gap: 6px; width: 190px; }
.filters label.production-time-filter { width: 370px; max-width: 100%; }
.production-orders > .ivu-card, .production-orders > .ivu-alert { margin-bottom: 16px; }
.summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 16px; }
.summary strong { display: block; font-size: 28px; color: #17233d; margin-top: 8px; }
.summary small { font-size: 13px; margin-left: 8px; color: #808695; font-weight: normal; }
.table-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 18px; gap: 12px; }
.create-form { display: flex; flex-wrap: wrap; margin-top: 18px; }
.create-form > .ivu-form-item { width: 50%; }
.create-form > .full { width: 100%; }
.create-form .ivu-input-number { width: 100%; }
.qr-preview { min-height: 200px; text-align: center; position: relative; }
.qr-preview img { width: 100%; max-width: 340px; }
@media (max-width: 700px) {
  .page-heading, .table-footer { flex-direction: column; align-items: stretch; }
  .summary { grid-template-columns: repeat(2, 1fr); }
  .create-form > .ivu-form-item { width: 100%; }
}
</style>
